import React, { useEffect, useRef, useState, useCallback } from "react";
import { Input, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { searchWorkspace } from "../../../../actions";
import _debounce from "lodash/debounce";
import { editTaskAction } from "../../../../redux/tasks/actions";
import { FormattedMessage } from "react-intl";

const TaskParentTaskSearch = ({ setShowParentTaskSearch, subTask }) => {
  const dispatch = useDispatch();
  const { search } = useSelector((state) => state.workspaceReducer);

  const [searchValue, setSearchValue] = useState("");
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);

  const inputRef = useRef(null);
  const itemRefs = useRef([]);

  const searchLoading = search?.isLoading;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const tasks = search?.results?.tasks || [];
    const filtered = tasks?.filter((task) => task?._id !== subTask?._id);
    setFilteredTasks(filtered);
    setHighlightIndex(0);
  }, [search?.results, subTask?._id]);

  useEffect(() => {
    if (itemRefs.current[highlightIndex]) {
      itemRefs.current[highlightIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightIndex]);

  const triggerSearch = (value) => {
    if (value.trim()) {
      setDropdownOpen(true);
      dispatch(searchWorkspace(value, "popover"));
    } else {
      setFilteredTasks([]);
      setDropdownOpen(false);
    }
  };

  const debouncedSearch = useCallback(
    _debounce((val) => triggerSearch(val), 400),
    []
  );

  const handleInputChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleSelectTask = (task) => {
    setSearchValue(task?.taskTitle || "");
    setDropdownOpen(false);
    const data = {
      _id: subTask?._id,
      parentTaskId: task?._id,
      assignee: subTask?.assignee?.map((a) => ({ assigneeId: a })),
    };

    dispatch(editTaskAction(data));
    setShowParentTaskSearch(false);
  };

  const handleKeyDown = (e) => {
    if (!dropdownOpen || filteredTasks.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredTasks.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredTasks.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredTasks[highlightIndex]) {
        handleSelectTask(filteredTasks[highlightIndex]);
      }
    }
  };

  return (
    <div className="flex flex-col bg-slate-50 -mx-4 px-4 gap-4 -mt-2 pb-5 relative">
      <div className="flex items-center justify-between mt-4">
        <FormattedMessage
          id="CONVERT_TASK_DESCRIPTION"
          defaultMessage="Make this task a subtask of another task"
        />
        <img
          src="/images/icons/close.svg"
          onClick={() => setShowParentTaskSearch(false)}
          className="size-3 -mt-3 cursor-pointer"
          alt="clear"
        />
      </div>
      <div className="relative w-full">
        <Input
          ref={inputRef}
          placeholder="Find a task"
          value={searchValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="w-full py-2"
        />
        {dropdownOpen && (
          <div
            className="absolute top-full left-0 bg-white border border-gray-200 shadow-md rounded-md mt-1 z-50"
            style={{
              width: "100%",
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {searchLoading ? (
              <div className="flex justify-center py-4">
                <Spin size="small" />
              </div>
            ) : filteredTasks?.length > 0 ? (
              filteredTasks?.map((task, idx) => (
                <div
                  key={task?._id}
                  ref={(el) => (itemRefs.current[idx] = el)}
                  onClick={() => handleSelectTask(task)}
                  className={`px-3 py-2 w-full flex items-center justify-between cursor-pointer 
                    ${idx === highlightIndex
                      ? "bg-slate-50"
                      : "hover:bg-slate-50"
                    }`}
                >
                  <div className="flex align-center truncate">
                    <img
                      alt="mark-complete"
                      src="/images/icons/completedSubtask.svg"
                      className={`size-4 mr-2 object-contain transition-opacity ${task.isCompleted ? "opacity-100" : "opacity-20"
                        }`}
                    />
                    <div className="font-12">{task?.taskTitle}</div>
                  </div>
                  <div className="font-10 text-nowrap pl-6 font-black">{task?.titleIdentifier}</div>
                </div>
              ))
            ) : searchValue.trim() ? (
              <div className="text-gray-500 text-center py-3">No tasks found</div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskParentTaskSearch;
